import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Shield, Search, Filter, Calendar, User, ChevronLeft, ChevronRight,
  Eye, Clock, Building2, FileText
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AuditLogsPage() {
  const { token, currentOutlet } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, page_size: 50, total: 0, total_pages: 0 });
  
  // Filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Filter options
  const [actionTypes, setActionTypes] = useState([]);
  const [users, setUsers] = useState([]);
  
  // Detail modal
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  // Fetch action types
  useEffect(() => {
    if (!token) return;
    fetch(`${API_URL}/api/audit/actions`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => r.json()).then(setActionTypes).catch(console.error);
  }, [token]);

  // Fetch users
  useEffect(() => {
    if (!token) return;
    fetch(`${API_URL}/api/audit/users`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => r.json()).then(setUsers).catch(console.error);
  }, [token]);

  const fetchLogs = useCallback(async (page = 1) => {
    if (!token) return;
    setLoading(true);
    
    const params = new URLSearchParams();
    params.set('page', page);
    params.set('page_size', '50');
    
    if (currentOutlet?.id) params.set('outlet_id', currentOutlet.id);
    if (startDate) params.set('start_date', startDate);
    if (endDate) params.set('end_date', endDate);
    if (actionFilter) params.set('action', actionFilter);
    if (userFilter) params.set('actor_id', userFilter);
    if (searchQuery) params.set('search', searchQuery);
    
    try {
      const res = await fetch(`${API_URL}/api/audit/logs?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setLogs(data.logs);
        setPagination(data.pagination);
      } else {
        toast.error('Failed to load audit logs');
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, [token, currentOutlet, startDate, endDate, actionFilter, userFilter, searchQuery]);

  useEffect(() => {
    fetchLogs(1);
  }, [fetchLogs]);

  const fetchLogDetail = async (logId) => {
    try {
      const res = await fetch(`${API_URL}/api/audit/log/${logId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setSelectedLog(await res.json());
        setShowDetail(true);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const getActionColor = (action) => {
    const colors = {
      void_item: 'text-red-400 bg-red-500/10',
      void_ticket: 'text-red-400 bg-red-500/10',
      refund: 'text-orange-400 bg-orange-500/10',
      discount: 'text-yellow-400 bg-yellow-500/10',
      cash_movement: 'text-blue-400 bg-blue-500/10',
      z_reading: 'text-purple-400 bg-purple-500/10',
      payment: 'text-green-400 bg-green-500/10',
      shift_open: 'text-teal-400 bg-teal-500/10',
      shift_close: 'text-teal-400 bg-teal-500/10',
    };
    return colors[action] || 'text-muted-foreground bg-muted';
  };

  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    setActionFilter('');
    setUserFilter('');
    setSearchQuery('');
    fetchLogs(1);
  };

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="audit-logs-page">
      {/* Header */}
      <header className="h-14 bg-card border-b border-border flex items-center justify-between px-6">
        <div className="flex items-center gap-3">
          <Shield className="w-6 h-6 text-teal-400" />
          <h1 className="text-xl font-semibold">Audit Logs</h1>
        </div>
        <span className="text-sm text-muted-foreground">{pagination.total} entries</span>
      </header>

      <div className="p-6 space-y-4">
        {/* Filters */}
        <div className="bg-card rounded-lg p-4 border border-border">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="text-xs text-muted-foreground mb-1 block">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search entity ID..."
                  className="pl-10 bg-muted border-border"
                  data-testid="audit-search"
                />
              </div>
            </div>
            
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Start Date</label>
              <Input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                className="w-36 bg-muted border-border"
              />
            </div>
            
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">End Date</label>
              <Input
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                className="w-36 bg-muted border-border"
              />
            </div>
            
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Action</label>
              <Select value={actionFilter || "all"} onValueChange={v => setActionFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="w-40 bg-muted border-border">
                  <SelectValue placeholder="All actions" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="all">All actions</SelectItem>
                  {actionTypes.map(a => (
                    <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">User</label>
              <Select value={userFilter || "all"} onValueChange={v => setUserFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="w-40 bg-muted border-border">
                  <SelectValue placeholder="All users" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="all">All users</SelectItem>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <Button variant="outline" onClick={handleClearFilters}>Clear</Button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-card rounded-lg border border-border">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="w-32">Action</TableHead>
                <TableHead>Actor</TableHead>
                <TableHead>Entity</TableHead>
                <TableHead>Changes</TableHead>
                <TableHead className="w-40">Timestamp</TableHead>
                <TableHead className="w-20"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No audit logs found
                  </TableCell>
                </TableRow>
              ) : (
                logs.map(log => (
                  <TableRow key={log.id} className="border-border">
                    <TableCell>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getActionColor(log.action)}`}>
                        {log.action_label}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{log.actor_name}</p>
                        <p className="text-xs text-muted-foreground">{log.actor_role}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="text-sm">{log.entity_type}</p>
                        <p className="text-xs text-muted-foreground font-mono">{log.entity_id.substring(0, 8)}...</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm text-muted-foreground truncate max-w-xs">{log.changes_summary}</p>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="text-sm">{log.timestamp ? new Date(log.timestamp).toLocaleDateString() : '-'}</p>
                        <p className="text-xs text-muted-foreground">{log.timestamp ? new Date(log.timestamp).toLocaleTimeString() : ''}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => fetchLogDetail(log.id)} data-testid={`view-log-${log.id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="flex justify-between items-center flex-wrap gap-2">
            <span className="text-sm text-muted-foreground">
              Page {pagination.page} of {pagination.total_pages}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchLogs(pagination.page - 1)}
                disabled={pagination.page <= 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchLogs(pagination.page + 1)}
                disabled={pagination.page >= pagination.total_pages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="bg-card border-border max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-teal-400" />
              Audit Log Detail
            </DialogTitle>
          </DialogHeader>
          
          {selectedLog && (
            <div className="space-y-4">
              {/* Action */}
              <div className="flex items-center gap-4">
                <span className={`px-3 py-1 rounded text-sm font-medium ${getActionColor(selectedLog.action)}`}>
                  {selectedLog.action_label}
                </span>
                <span className="text-sm text-muted-foreground">{selectedLog.timestamp && new Date(selectedLog.timestamp).toLocaleString()}</span>
              </div>

              {/* Actor */}
              <div className="bg-muted rounded-lg p-4">
                <div className="flex items-center gap-3 mb-2">
                  <User className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Performed by</span>
                </div>
                <p className="font-medium">{selectedLog.actor?.name}</p>
                <p className="text-sm text-muted-foreground">{selectedLog.actor?.email} • {selectedLog.actor?.role}</p>
              </div>

              {/* Approver if exists */}
              {selectedLog.approver && (
                <div className="bg-muted rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <Shield className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Approved by</span>
                  </div>
                  <p className="font-medium">{selectedLog.approver.name}</p>
                  <p className="text-sm text-muted-foreground">{selectedLog.approver.role}</p>
                </div>
              )}

              {/* Entity */}
              <div className="bg-muted rounded-lg p-4">
                <div className="flex items-center gap-3 mb-2">
                  <Building2 className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Entity</span>
                </div>
                <p className="font-medium">{selectedLog.entity_type}</p>
                <p className="text-sm text-muted-foreground font-mono">{selectedLog.entity_id}</p>
              </div>

              {/* Changes */}
              {Object.keys(selectedLog.changes || {}).length > 0 && (
                <div className="bg-muted rounded-lg p-4">
                  <h4 className="text-sm text-muted-foreground mb-2">Changes</h4>
                  <div className="space-y-2">
                    {Object.entries(selectedLog.changes).map(([key, value]) => (
                      <div key={key} className="flex justify-between text-sm">
                        <span className="text-muted-foreground">{key}</span>
                        <span>
                          {typeof value === 'object' && value.old !== undefined ? (
                            <>
                              <span className="text-red-400 line-through mr-2">{String(value.old)}</span>
                              <span className="text-green-400">{String(value.new)}</span>
                            </>
                          ) : (
                            String(value)
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Metadata */}
              {Object.keys(selectedLog.metadata || {}).length > 0 && (
                <div className="bg-muted rounded-lg p-4">
                  <h4 className="text-sm text-muted-foreground mb-2">Additional Info</h4>
                  <div className="space-y-1 text-sm">
                    {selectedLog.metadata.reason && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Reason</span>
                        <span>{selectedLog.metadata.reason}</span>
                      </div>
                    )}
                    {selectedLog.metadata.amount && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Amount</span>
                        <span>₱{selectedLog.metadata.amount.toFixed(2)}</span>
                      </div>
                    )}
                    {selectedLog.metadata.ip_address && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">IP Address</span>
                        <span className="font-mono">{selectedLog.metadata.ip_address}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
